// Pure geometry helpers for the custom polygon editor: a saved shape in, GeoJSON features and rings,
// bounds, and the self-intersection check run before save. Reading a saved shape is the area shapes
// module's job; this file keeps the editing rules. No React and no drawing-library imports.
import { readShape } from '../geo/areaShapes';
import type { ShapeInput } from '../geo/areaShapes';

/** An outer ring of [longitude, latitude] points, closed: the first point equals the last. */
export type Ring = [number, number][];

export interface PolygonFeature {
  type: 'Feature';
  geometry: { type: 'Polygon'; coordinates: number[][][] };
  properties: { mode: 'polygon' };
}

const same = (a: number[], b: number[]) => a[0] === b[0] && a[1] === b[1];

/** The outer ring as editor points: needs 3 or more points, and is closed. */
function toRing(coordinates: number[][]): Ring | null {
  const points: Ring = coordinates.map(([x, y]) => [x, y]);
  if (points.length < 3) return null;
  if (!same(points[0], points[points.length - 1])) points.push([points[0][0], points[0][1]]);
  return points;
}

/**
 * The outer ring of a saved polygon, read through the area shapes module (so WKT text, the `{ value }`
 * wrapper and GeoJSON all work). Holes are dropped and reported. Null for anything that is not a single
 * polygon (a multipolygon, a line, empty, unreadable) or has fewer than 3 points.
 */
export function shapeToRing(input: ShapeInput): { ring: Ring; droppedHoles: boolean } | null {
  const shape = readShape(input);
  if (!shape || shape.type !== 'Polygon') return null;
  const ring = toRing(shape.coordinates[0]);
  if (!ring) return null;
  return { ring, droppedHoles: shape.coordinates.length > 1 };
}

/** A GeoJSON polygon feature; the mode property names the drawing mode that owns it. */
export function ringToFeature(ring: Ring, id?: string): PolygonFeature & { id?: string } {
  const feature: PolygonFeature & { id?: string } = {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [ring.map(([x, y]) => [x, y])] },
    properties: { mode: 'polygon' },
  };
  if (id !== undefined) feature.id = id;
  return feature;
}

/** The outer ring of a polygon feature, closed, coordinates unchanged. */
export function featureToRing(feature: { geometry: { coordinates: number[][][] | unknown } }): Ring {
  const outer = ((feature.geometry.coordinates as number[][][])[0] ?? []).map(([x, y]) => [x, y] as [number, number]);
  if (outer.length > 0 && !same(outer[0], outer[outer.length - 1])) outer.push([outer[0][0], outer[0][1]]);
  return outer;
}

/** Decimal places the editor keeps. terra-draw refuses a coordinate with more than this many. */
export const COORDINATE_PRECISION = 9;

/** Vertex options for polygons in select mode: edge midpoints can be dragged out into new vertices. */
export const POLYGON_COORDINATE_FLAGS = {
  midpoints: { draggable: true },
  draggable: true,
  deletable: true,
} as const;

/**
 * The ring with every coordinate rounded to `digits` decimals, a point that repeats the one before
 * it dropped, and the ring closed again. A ring already within `digits` comes back unchanged.
 */
export function roundRing(ring: Ring, digits = COORDINATE_PRECISION): Ring {
  const out: Ring = [];
  for (const [lng, lat] of ring) {
    const p: [number, number] = [Number(lng.toFixed(digits)), Number(lat.toFixed(digits))];
    if (out.length > 0 && same(out[out.length - 1], p)) continue;
    out.push(p);
  }
  if (out.length > 0 && !same(out[0], out[out.length - 1])) out.push([out[0][0], out[0][1]]);
  return out;
}

export type Bounds = [[number, number], [number, number]];

export function ringBounds(ring: Ring): Bounds {
  return ringsBounds([ring]);
}

export function ringsBounds(rings: Ring[]): Bounds {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

type P = [number, number];

const orient = (a: P, b: P, c: P) => {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  return v > 0 ? 1 : v < 0 ? -1 : 0;
};

const onSegment = (a: P, b: P, c: P) =>
  Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1]);

function segmentsIntersect(p1: P, p2: P, p3: P, p4: P): boolean {
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, p3)) return true;
  if (o2 === 0 && onSegment(p1, p2, p4)) return true;
  if (o3 === 0 && onSegment(p3, p4, p1)) return true;
  if (o4 === 0 && onSegment(p3, p4, p2)) return true;
  return false;
}

/** True when two non-adjacent edges of the ring cross or touch. Adjacent edges share an end and do not count. */
export function ringSelfIntersects(ring: Ring): boolean {
  const n = ring.length - 1; // edges; the last point repeats the first
  if (n < 4) return false; // a triangle cannot cross itself
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsIntersect(ring[i], ring[i + 1], ring[j], ring[j + 1])) return true;
    }
  }
  return false;
}

export type RingValidation = { ok: true } | { ok: false; reason: 'too-few-points' | 'self-intersecting' };

export function validateRing(ring: Ring): RingValidation {
  const distinct = new Set(ring.map(([x, y]) => `${x} ${y}`));
  if (distinct.size < 3) return { ok: false, reason: 'too-few-points' };
  if (ringSelfIntersects(ring)) return { ok: false, reason: 'self-intersecting' };
  return { ok: true };
}
